import type { Preferences } from '@cobliteam/common-types';
import Typography from '@cobliteam/ui/components/Typography';
import { cn } from '@cobliteam/ui/utils';
import React, { createContext, useContext, useMemo, useRef } from 'react';
import ShineBorder from '../border/ShineBorder';

interface InfoCardTitleProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

interface InfoCardDescriptionProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const InfoCardTitle = React.memo(({ children, className }: InfoCardTitleProps) => {
  return (
    <Typography data-testid='info-card-title' variant='heading-2' className={cn('mb-1', className)}>
      {children}
    </Typography>
  );
});
InfoCardTitle.displayName = 'InfoCardTitle';

const InfoCardDescription = React.memo(({ children, className, ...props }: InfoCardDescriptionProps) => {
  return (
    <Typography data-testid='info-card-description' variant='text-md' className={className} {...props} color='secondary'>
      {children}
    </Typography>
  );
});
InfoCardDescription.displayName = 'InfoCardDescription';

interface CommonCardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

interface InfoCardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  storageKey?: keyof Preferences;
  dismissType?: 'once' | 'forever';
  shineBorder?: boolean;
  onDismiss?: () => void;
}

type InfoCardContentProps = CommonCardProps;
type InfoCardFooterProps = CommonCardProps;
type InfoCardDismissProps = React.HTMLAttributes<HTMLDivElement> & {
  children: React.ReactNode;
  onDismiss?: () => void;
};
type InfoCardActionProps = CommonCardProps;

const InfoCardContent = React.memo(({ children, className, ...props }: InfoCardContentProps) => {
  return (
    <div data-testid='info-card-content' className={cn('flex flex-col gap-1 bg-white text-sm', className)} {...props}>
      {children}
    </div>
  );
});
InfoCardContent.displayName = 'InfoCardContent';

interface MediaItem {
  type?: 'image' | 'video' | 'youtube';
  src: string;
  alt?: string;
  className?: string;
  [key: string]: any;
}

interface InfoCardMediaProps extends React.HTMLAttributes<HTMLDivElement> {
  media: MediaItem[];
  loading?: 'eager' | 'lazy';
  shrinkHeight?: number;
  expandHeight?: number;
}

const InfoCardImageContext = createContext<{
  handleMediaLoad: (mediaSrc: string) => void;
  setAllImagesLoaded: (loaded: boolean) => void;
}>({
  handleMediaLoad: () => {},
  setAllImagesLoaded: () => {},
});

const InfoCardContext = createContext<{
  onDismiss?: () => void;
}>({
  onDismiss: () => {},
});

function InfoCard({ children, className, shineBorder = true, onDismiss }: InfoCardProps) {
  const cardContextValue = useMemo(
    () => ({
      onDismiss: onDismiss,
    }),
    [onDismiss]
  );

  return (
    <InfoCardContext.Provider value={cardContextValue}>
      <div data-testid='info-card' className={cn('group rounded-xl border-2 border-gray-5 bg-white p-3 shadow-xl', className)}>
        {shineBorder && <ShineBorder shineColor={['transparent', 'transparent', '#A07CFE', '#FE8FB5', '#FFBE7B', 'transparent', 'transparent']} borderWidth={2} />}
        {children}
      </div>
    </InfoCardContext.Provider>
  );
}

const InfoCardFooter = ({ children, className }: InfoCardFooterProps) => {
  return (
    <div data-testid='info-card-footer' className={cn('mt-2 flex justify-between text-muted-foreground text-xs', className)}>
      {children}
    </div>
  );
};

const InfoCardDismiss = React.memo(({ children, className, onDismiss, ...props }: InfoCardDismissProps) => {
  const { onDismiss: contextDismiss } = useContext(InfoCardContext);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onDismiss?.();
    contextDismiss?.();
  };

  return (
    <div className={cn('cursor-pointer', className)} onClick={handleClick} {...props}>
      {children}
    </div>
  );
});
InfoCardDismiss.displayName = 'InfoCardDismiss';

const InfoCardAction = React.memo(({ children, className, ...props }: InfoCardActionProps) => {
  return (
    <div className={cn('', className)} {...props}>
      {children}
    </div>
  );
});
InfoCardAction.displayName = 'InfoCardAction';

const InfoCardMedia = ({ media = [], className, loading = undefined }: InfoCardMediaProps) => {
  const { setAllImagesLoaded } = useContext(InfoCardImageContext);
  const loadedMedia = useRef(new Set());

  const handleMediaLoad = (mediaSrc: string) => {
    loadedMedia.current.add(mediaSrc);
    if (loadedMedia.current.size === Math.min(3, media.slice(0, 3).length)) {
      setAllImagesLoaded(true);
    }
  };

  const processedMedia = useMemo(
    () =>
      media.map((item) => ({
        ...item,
        type: item.type || 'image',
      })),
    [media]
  );

  const displayMedia = useMemo(() => processedMedia.slice(0, 3), [processedMedia]);
  const getYouTubeEmbedUrl = (url: string) => {
    const videoId =
      url.includes('youtube.com') || url.includes('youtu.be') ? url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/)?.[1] : url;

    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  };

  return (
    <div data-testid='info-card-media' className={cn('relative mt-2 rounded-md', className)}>
      <div className={cn('relative z-10 h-auto')}>
        {displayMedia.map((item, index) => {
          const { type, src, alt, className: itemClassName, ...mediaProps } = item;

          return (
            <div key={src} className='w-full'>
              {type === 'youtube' ? (
                <iframe
                  src={getYouTubeEmbedUrl(src)}
                  className={cn('w-full rounded-md border border-gray-4 shadow-lg', itemClassName)}
                  style={{ aspectRatio: '16/9' }}
                  allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                  allowFullScreen
                  onLoad={() => handleMediaLoad(src)}
                  {...mediaProps}
                />
              ) : type === 'video' ? (
                <video
                  src={src}
                  className={cn('w-full rounded-md border border-gray-4 object-cover shadow-lg', itemClassName)}
                  onLoadedData={() => handleMediaLoad(src)}
                  preload='metadata'
                  muted
                  playsInline
                  {...mediaProps}
                />
              ) : (
                <img
                  src={src}
                  alt={alt}
                  className={cn('w-full rounded-md border border-gray-4 object-cover shadow-lg', itemClassName)}
                  onLoad={() => handleMediaLoad(src)}
                  loading={loading}
                  {...mediaProps}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { InfoCard, InfoCardAction, InfoCardContent, InfoCardDescription, InfoCardDismiss, InfoCardFooter, InfoCardMedia, InfoCardTitle };
